import type { FormaRent } from '../core/FormaRent';

interface AnalyticsEvent {
  userId: string;
  /** Ключ витрины партнёра: на owner-конфиге userId пустой, атрибуция идёт сюда */
  ownerKey?: string;
  sessionId: string;
  event: string;
  payload: Record<string, unknown>;
  timestamp: number;
}

const SESSION_KEY = 'forma_rent_session';
const BATCH_INTERVAL = 2000;
const MAX_BATCH = 10;

function uuid(): string | null {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    // v4 UUID: устанавливаем version и variant биты
    bytes[6] = (bytes[6]! & 0x0f) | 0x40;
    bytes[8] = (bytes[8]! & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  return null;
}

function getSessionId(): string | null {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = uuid();
      if (!id) return null;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return uuid();
  }
}

export function installAnalytics(instance: FormaRent): () => void {
  const cfg = instance._config;
  if (!cfg) return () => {};

  const userId = cfg.user ?? '';
  const ownerKey = cfg.owner;
  const baseUrl = (cfg.baseUrl ?? __FORMA_ORIGIN__).replace(/\/$/, '');
  const sessionId = getSessionId();
  if (!sessionId) {
    // Без crypto-рандома не шлём аналитику вообще — предсказуемый sessionId хуже чем отсутствие данных.
    return () => {};
  }
  const queue: AnalyticsEvent[] = [];
  let openedAt = 0;

  const flush = (useBeacon: boolean): void => {
    if (queue.length === 0) return;
    const body = JSON.stringify({ events: queue.splice(0) });
    const url = `${baseUrl}/api/embed/events`;
    try {
      if (useBeacon && typeof navigator.sendBeacon === 'function') {
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      } else {
        void fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true,
        }).catch(() => {});
      }
    } catch {
      // best-effort
    }
  };

  const enqueue = (event: string, payload: Record<string, unknown> = {}): void => {
    queue.push({
      userId,
      ownerKey,
      sessionId,
      event,
      payload: {
        origin: window.location.origin,
        referrer: document.referrer || undefined,
        ...payload,
      },
      timestamp: Date.now(),
    });
    if (queue.length >= MAX_BATCH) flush(false);
  };

  enqueue('widget_loaded');

  const unsubscribers: Array<() => void> = [];
  unsubscribers.push(
    instance.on('open', (params) => {
      openedAt = Date.now();
      enqueue('modal_opened', { ...params });
    }),
    instance.on('close', () => {
      enqueue('modal_closed', { durationMs: Date.now() - openedAt });
    }),
    instance.on('booking:start', (p) => enqueue('booking_started', { ...p })),
    instance.on('booking:done', (p) => enqueue('booking_completed', { ...p })),
    instance.on('error', (p) => enqueue('error', { errorCode: p.code })),
  );

  const intervalId = setInterval(() => flush(false), BATCH_INTERVAL);
  const unloadHandler = (): void => flush(true);
  window.addEventListener('beforeunload', unloadHandler);

  return (): void => {
    clearInterval(intervalId);
    window.removeEventListener('beforeunload', unloadHandler);
    for (const unsub of unsubscribers) unsub();
    flush(true);
  };
}
